import { RouteTextOverrideArgs } from 'types';
import { ApolloClient } from '@apollo/client';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import GET_RESOURCE_TYPE_CACHE_REDIRECT from 'graph/resource-type/resource-type-cache-redirect-query';
import { getAssessmentAcceleratorTitle } from '../util';
import { ProductCurriculum, ProductFamily } from 'types/schema-generated-types';

export const DEFAULT_NAME = 'Teaching Resources';

const ROUTE_PREFIX = '/resources';
const BASE_BREADCRUMB = {
  name: DEFAULT_NAME,
  path: ROUTE_PREFIX,
  Icon: LibraryBooksIcon
};

const getProductCurriculumBreadcrumbName = (
  productCurriculum: ProductCurriculum,
  productFamily: ProductFamily
) => {
  let breadcrumbName = `${productFamily.name}`;
  if (productCurriculum.gradeLevel) {
    breadcrumbName += ` - Grade ${productCurriculum.gradeLevel.join(', ')}`;
  }

  return breadcrumbName;
};

export const getResourceTypeById = (client: ApolloClient<any>, id: number): any => {
  let result;

  try {
    result = client.readQuery({
      query: GET_RESOURCE_TYPE_CACHE_REDIRECT,
      variables: { id }
    });
  } catch (err) {
    // do nothing
  }

  return result;
};

export const getBreadcrumbs = ({ match, client }: RouteTextOverrideArgs): any => {
  const resourceTypeId = +match?.params?.resourceTypeId;
  const resourceType = getResourceTypeById(client, resourceTypeId);

  return [
    BASE_BREADCRUMB,
    {
      name: resourceType?.resourceTypeDetail?.title ?? 'Resources',
      path: `${ROUTE_PREFIX}/${resourceTypeId}`
    }
  ];
};

export const getResourceProductCurriculumBreadcrumbs = ({
  match,
  client,
  productCache
}: RouteTextOverrideArgs) => {
  const productCurriculumCode = match?.params?.productCurriculumCode;
  const productCurriculum = productCache?.findProductCurriculumByCode(productCurriculumCode);
  const productFamily = productCache?.findProductFamilyByProductCurriculumCode(
    productCurriculumCode
  );

  if (!productCurriculum || !productFamily) return [BASE_BREADCRUMB];
  const name = getProductCurriculumBreadcrumbName(productCurriculum, productFamily);

  // if user navigated from Resource
  const resourceTypeId = +match?.params?.resourceTypeId;
  const resourceType = getResourceTypeById(client, resourceTypeId);
  if (resourceType) {
    return [
      BASE_BREADCRUMB,
      {
        name: resourceType.resourceTypeDetail?.title ?? DEFAULT_NAME,
        path: `${ROUTE_PREFIX}/${resourceTypeId}`
      },
      {
        name,
        path: `${ROUTE_PREFIX}/${resourceTypeId}/${productCurriculum.code}`
      }
    ];
  }

  // if user navigated from direct route (Resource not available in cache)
  return [BASE_BREADCRUMB, { name, path: `${ROUTE_PREFIX}/${productCurriculum.code}` }];
};

export const getResourceProductCurriculumWeekBreadcrumbs = ({
  match,
  client,
  productCache
}: RouteTextOverrideArgs) => {
  const { productCurriculumCode, week } = match?.params ?? {};

  const productCurriculum = productCache?.findProductCurriculumByCode(productCurriculumCode);
  const productFamily = productCache?.findProductFamilyByProductCurriculumCode(
    productCurriculumCode
  );

  if (!productCurriculum || !productFamily) return [BASE_BREADCRUMB];
  const name = getProductCurriculumBreadcrumbName(productCurriculum, productFamily);

  // if user navigated from Resource
  const resourceTypeId = +match?.params?.resourceTypeId;
  const resourceType = getResourceTypeById(client, resourceTypeId);
  if (resourceType) {
    return [
      BASE_BREADCRUMB,
      {
        name: resourceType?.resourceTypeDetail?.title ?? DEFAULT_NAME,
        path: `${ROUTE_PREFIX}/${resourceTypeId}`
      },
      {
        name,
        path: `${ROUTE_PREFIX}/${resourceTypeId}/${productCurriculum.code}`
      },
      {
        name: `Week ${week}`,
        path: `${ROUTE_PREFIX}/${resourceTypeId}/${productCurriculum.code}/${week}`
      }
    ];
  }

  // if user navigated from direct route (Resource not available in cache)
  return [
    BASE_BREADCRUMB,
    {
      name,
      path: `${ROUTE_PREFIX}/${resourceTypeId}/${productCurriculum.code}`
    },
    {
      name: `Week ${week}`,
      path: `${ROUTE_PREFIX}/${resourceTypeId}/${productCurriculum.code}/${week}`
    }
  ];
};

export const getAssessmentAcceleratorBreadcrumbs = ({ match }: RouteTextOverrideArgs) => {
  const { productCurriculumCode } = match?.params ?? {};
  const name = getAssessmentAcceleratorTitle(productCurriculumCode);

  return [
    BASE_BREADCRUMB,
    {
      name: 'Assessment Accelerator',
      path: `${ROUTE_PREFIX}/5`
    },
    {
      name,
      path: `${ROUTE_PREFIX}/5/${productCurriculumCode}`
    }
  ];
};
